import type { Platform, PlatformPostData, PostMedia, PostMessage, MobileForensicPlatformPost, MobileForensicThreadSummary } from '@tranquility-ai/theme-components';
import type { MobileFrontEndBubbleMessageView, MobileUiConfigArtifact } from '@/shared/mobile-service/api/cors-mobile-api';
import type { UfdrChatSummary } from './legacy-types';

/**
 * App name (lowercased, symbols stripped) to a PlatformPost platform.
 * An unknown app uses `dataSources`, whose generic archetype renders a thread
 * when `messages` is set. It does not fall back to `smsAndroid`.
 */
export const PLATFORM_BY_APP: Record<string, Platform> = {
  sms: 'smsAndroid',
  android: 'smsAndroid',
  imessage: 'smsIos',
  ios: 'smsIos',
  whatsapp: 'whatsapp',
  signal: 'signal',
  telegram: 'telegram',
  messenger: 'messenger',
  facebookmessenger: 'messenger',
  instagram: 'instagramDm',
  instagramdm: 'instagramDm',
  snapchat: 'snapchat',
  discord: 'discord',
  slack: 'slack',
  teams: 'teams',
  wechat: 'wechat',
  line: 'line',
  viber: 'viber',
  threema: 'threema',
  kakaotalk: 'kakaotalk',
  googlechat: 'googleChat',
  rocketchat: 'rocketChat',
  facebook: 'facebook',
  twitter: 'twitter',
  x: 'twitter',
  tiktok: 'tiktok',
  reddit: 'reddit',
  linkedin: 'linkedin',
  youtube: 'youtube',
};

export const GENERIC_PLATFORM: Platform = 'dataSources';

export function normalizeAppKey(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function platformForArtifact(artifact: Pick<MobileUiConfigArtifact, 'app' | 'platforms' | 'title' | 'graphql_type'>): Platform {
  const candidates = [artifact.app, ...(artifact.platforms ?? []), artifact.title, artifact.graphql_type];
  for (const candidate of candidates) {
    const key = normalizeAppKey(candidate);
    if (key && PLATFORM_BY_APP[key]) return PLATFORM_BY_APP[key];
    for (const [name, platform] of Object.entries(PLATFORM_BY_APP)) {
      if (key.includes(name)) return platform;
    }
  }
  return GENERIC_PLATFORM;
}

function readPath(row: Record<string, unknown>, path: string | undefined): unknown {
  if (!path) return undefined;
  let current: unknown = row;
  for (const part of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function mediaType(mime: string | undefined): PostMedia['type'] {
  if (!mime) return 'file';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'file';
}

function attachments(row: Record<string, unknown>, view: MobileFrontEndBubbleMessageView): PostMedia[] {
  const media: PostMedia[] = [];
  for (const source of view.attachments ?? []) {
    const items = readPath(row, source.items_field);
    const list = Array.isArray(items) ? items : items && typeof items === 'object' ? [items] : [];
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const record = item as Record<string, unknown>;
      const url = asString(readPath(record, source.media_field));
      if (!url) continue;
      const mime = asString(source.mime_field ? readPath(record, source.mime_field) : undefined);
      media.push({
        type: mediaType(mime),
        url,
        filename: asString(source.filename_field ? readPath(record, source.filename_field) : undefined),
      });
    }
  }
  return media;
}

function isOutgoing(row: Record<string, unknown>, view: MobileFrontEndBubbleMessageView): boolean {
  const direction = view.direction;
  if (!direction) return false;
  if (direction.owner_field && readPath(row, direction.owner_field) === true) return true;
  const value = asString(direction.field ? readPath(row, direction.field) : undefined);
  return Boolean(value && direction.outgoing_values?.includes(value));
}

export function rowToPostMessage(row: Record<string, unknown>, view: MobileFrontEndBubbleMessageView): PostMessage {
  const deleted = readPath(row, 'deleted') === true || asString(readPath(row, 'deletionState')) != null;
  return {
    id: asString(readPath(row, 'mobileArtifactId')) ?? asString(readPath(row, 'id')),
    direction: isOutgoing(row, view) ? 'outgoing' : 'incoming',
    text: asString(readPath(row, view.body_field)),
    timestamp: asString(readPath(row, view.timestamp_field)),
    sender: { name: asString(view.sender_name_field ? readPath(row, view.sender_name_field) : undefined) },
    media: attachments(row, view),
    deleted,
    kind: 'message',
  };
}

/** The only mapper from a ui-config message artifact onto PlatformPost. */
export function toPlatformPost(
  rows: Array<Record<string, unknown>>,
  artifact: MobileUiConfigArtifact,
  view: MobileFrontEndBubbleMessageView,
  peer?: string,
): { platform: Platform; label?: string; data: PlatformPostData } {
  const platform = platformForArtifact(artifact);
  const messages = rows.map((row) => rowToPostMessage(row, view));
  return {
    platform,
    label: platform === GENERIC_PLATFORM ? artifact.app || artifact.title || artifact.graphql_type : undefined,
    data: {
      author: { name: peer || artifact.title || artifact.graphql_type },
      messages,
    },
  };
}

/** A chat parsed from export files, summarized for the thread list. */
export function threadSummary(chat: UfdrChatSummary): MobileForensicThreadSummary {
  const platform = platformForArtifact({
    graphql_type: chat.app,
    app: chat.app,
    title: chat.app,
  });
  return {
    id: chat.id,
    title: chat.peer,
    platform,
    label: platform === GENERIC_PLATFORM ? chat.app : undefined,
    lastMessage: chat.lastMessage,
    timestamp: chat.timestamp,
    count: chat.messageCount,
  };
}

/** The only mapper from an export-file chat onto PlatformPost. */
export function threadPost(chat: UfdrChatSummary): MobileForensicPlatformPost & { id: string } {
  const summary = threadSummary(chat);
  return {
    id: chat.id,
    platform: summary.platform,
    label: summary.label,
    data: {
      author: { name: chat.peer },
      messages: (chat.messages ?? []).map((message) => ({
        id: message.id,
        direction: message.direction,
        text: message.text,
        timestamp: message.timestamp,
        status: message.status,
        sender: message.sender ? { name: message.sender } : undefined,
        media: message.attachments?.map((attachment) => ({
          type: attachment.type === 'link' ? 'file' as const : attachment.type,
          url: attachment.url,
          thumbnailUrl: attachment.thumbnailUrl,
          alt: attachment.alt,
        })),
      })),
    },
  };
}
